import { IBaseEntity, INode } from './base';
import { IText, ITextDAO } from './text';
import { IImage, IImageDAO } from './image';
import { IModule, IModuleBasicDAO, IModuleDAO } from './module';
import { IVerificationDAO } from './verification';
import { ICertificate } from './certificate';
import { IGroup } from './group';
import { IService } from './service';
import { IOrganisationSystemDAO, ISystem } from './system';
import { IVariableDAO } from './variable';
import { CountByDate } from './organisation';
import { ICoursePermission, ICourseProgress, ICourseProgressDAO } from './permissions';
import { EntityTypes } from 'constants/entities';
import { IOrgUserVariable, IOrgUserVariableDAO } from './user';
import { ISessionAttempt } from './session';

export interface ICourse extends IBaseEntity {
  copyId: number;
  name: string;
  textId: number;
  imageId: number;
  folderId: number;
  variableId: number;
  type: CourseType;
  status: CourseStatus;
  launchCampaign: boolean;
  launchDate: string;
  progress: number;
  courseNodeId: number;
  courseProgressCount?: number;
  courseProgressActiveCount: number;
  courseProgressCompleteCount: number;
  courseProgressCountChange?: number;
  courseProgressMobileCount?: number;
  courseProgressActiveCountChange?: number;
  courseProgressCompleteCountChange?: number;
  organisationUserVariableCount: number;
  statsTotalTime: number;
  forumTopicCount?: number;
  moduleCount?: number;
}

export interface ICourseDAO extends ICourse {
  text: IText;
  image: IImage;
  outs: ICourseOut[];
  nodes: ICourseNode[];
  courseVariables: ICourseVariableDAO[];
  coursePermission?: ICoursePermission;
  latestCourseProgress?: ICourseProgress;
}

export enum CourseType {
  STANDARD = 0,
}
export enum CourseStatus {
  DRAFT = 0,
  LIVE = 1,
  PAUSED = 2,
}

export interface ICourseNode extends IBaseEntity {
  type: CourseNodeType;
  status: CourseNodeStatus;
  entityType: CourseNodeRole;
  courseId: number;
  childId: number;
  nodeId: number;
  moduleId: number;
  moduleVersionId: number;
  verificationId: number;
  systemId: number;
  organisationSystemId: number;
  variableId: number;
  serviceId: number;
  groupId: number;
  certificateId: number;
  textId: number;
  imageId: number;
  uploadId?: string;
  token?: string;
  prettyToken?: string;
  iconId: number;
  learnerCount?: number;
  certificateCount?: number;
  certificateCompleteCount?: number;
  variableCount?: number;
  responseNewCount?: number;
  joinCourseNode: ICourseNode;
  delay: number;
  organisationUserVariablesCount: number;
  sessionAttemptCount: number;
  sessionAttemptProgressCount: number;
  sessionAttemptCompleteCount: number;
  sessionAttemptTotalTime: number;
  sessionAttemptTotalProgress: number;
  courseProgressCount: number;
  variableTotalScore: number;
  home?: number;
  themeId?: number;
  courseProgressCurrent?: ICourseProgressDAO[];
  courseProgressEntry?: ICourseProgressDAO[];
  limit?: number;
}

export enum CourseNodeType {
  START = 0,
  ASSIGN = 1,
  LOGIC = 2,
  FILTER = 3,
  REQUEST = 4,
  RESPONSE = 5,
  RANDOM = 6,
  END = 7,
  NOTIFICATION = 8,
  INTEGRATION = 9,
  MENU = 10,
}

export enum CourseNodeRole {
  ACCESS = -1,
  MODULE = 0,
  GROUP = 1,
  VARIABLE = 2,
  VERIFICATION = 3,
  SYSTEM = 4,
  SERVICE = 5,
  TEXT = 6,
  SCENE = 7,
  DIALOGUE = 8,
  SHARE = 9,
  SCORM = 10,
  API = 11,
  EMAIL = 12,
  PHOTO = 13,
  VIDEO = 14,
  NAME = 15,
  SYSTEM_ID = 16,
  DOCUMENT = 17,
  CERTIFICATE = 18,
  ACCOUNT = 19,
  MENU = 20,
  VARIABLE_SINGLE = 21,
}

export enum CourseNodeStatus {
  DELETED = -1,
  DRAFT = 0,
  PRIVATE = 1,
  PUBLIC = 2,
  IDENTITY = 3,
  SELF_SIGNUP = 4,
  ARCHIVED = 5,
  INHERIT = 6,
}

export interface ICourseNodeDAO extends ICourseNode {
  courseId: number;
  node: INode;
  module: IModuleBasicDAO;
  verification: IVerificationDAO;
  system: ISystem;
  organisationSystem: IOrganisationSystemDAO;
  variable: IVariableDAO;
  service: IService;
  group: IGroup;
  certificate: ICertificate;
  text: ITextDAO;
  image: IImageDAO;
  entities: ICourseNodeEntityDAO[];
  courseOuts: ICourseOutDAO[];
  isDefault?: boolean;
  courseVariable?: ICourseVariable;
  organisationUserVariableId?: number;
  sessionAttempt?: ISessionAttempt;
  organisationUserVariable?: IOrgUserVariable;
  organisationUserVariables: IOrgUserVariable[];
  courseVariables: ICourseVariable[];
  courseUserVariables: ICourseUserVariable[];
  courseNodeTargets: ICourseOut[];
  courseNodeGroups: ICourseNodeGroupDAO[];
  avgSessionAttemptTime: number | null;
}

export interface ICourseFlowNode {
  id: string;
  depth: number;
  data: any;
  children: ICourseFlowNode[];
  index: number;
  source: string;
  sourceHandle: string;
  type: EntityTypes;
  height: number;
  position: { x: number; y: number };
}

export interface IModuleCourseNode extends ICourseNode {
  node: INode;
  text: ITextDAO;
  image: IImageDAO;
  module: IModuleDAO;
}

export interface ICourseOut extends IBaseEntity {
  sourceId: number;
  sourceHandle: number;
  targetId: number;
  targetHandle: number;
  delay: number;
  status: CourseOutStatus;
  start?: number;
  end?: number;
  courseId: number;
}

export interface ICourseNodeGroup extends IBaseEntity {
  courseNodeId: number;
  groupId: number;
}
export interface ICourseNodeGroupDAO extends ICourseNodeGroup {
  group: IGroup;
}

export interface ICourseOutDAO extends ICourseOut {
  source?: ICourseNodeDAO;
  target?: ICourseNodeDAO;
}

export enum CourseOutType {
  STANDARD = 0,
  DELAY = 1,
  REMINDER = 2,
}
export enum CourseOutStatus {
  NEW = 0,
  COMPLETE = 1,
  PROCESSED = 2,
}

export interface ICourseNodeEntity extends IBaseEntity {
  type: CourseNodeEntityType;
  courseNodeId: number;
  textId: number;
  order: number;
  verificationId: number;
  variableId: number;
  variableOptionId: number;
  groupId: number;
  certificateId: number;
  name: string;
  operator: number;
  value: number;
  delay: number;
  minValue: number;
  maxValue: number;
  outs: ICourseOut[];
}

export interface ICourseNodeEntityDAO extends ICourseNodeEntity {
  group: IGroup;
  variable: IVariableDAO;
  text: IText;
}

export enum CourseNodeEntityType {
  BRANCH = 0,
  REMIND = 1,
}

export interface ICourseReport extends ICourse {
  range: string;
  invitesByDate: CountByDate[];
  startedByDate: CountByDate[];
  completedByDate: CountByDate[];
  totalPeople: number;
  totalNotStarted: number;
  totalStarted: number;
  totalCompleted: number;
}

export interface ICourseVariable extends IBaseEntity {
  certificateId: number;
  courseId: number;
  variableId: number;
  variableOptionId: number;
  value: number;
  order: number;
}

export interface ICourseVariableDAO extends ICourseVariable {
  variable: IVariableDAO;
  modules: IModule[];
  variableCount?: number;
  variableCompleteCount?: number;
  variableTotalScore: number;
}

export interface ICourseVariableReport extends ICourseVariableDAO {}

export interface ICourseUserVariable extends IBaseEntity {
  courseNodeId: number;
  organisationUserVariableId: number;
  courseVariableId: number;
  organisationUserVariable: IOrgUserVariable;
  courseVariable: ICourseVariable;
}

export interface ICourseUserVariableDAO extends ICourseUserVariable {
  organisationUserVariable: IOrgUserVariableDAO;
  courseVariable: ICourseVariableDAO;
}
